import { useState, useEffect, createContext, useContext } from 'react';
import { getMe, login as apiLogin } from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('ry_token');
    if (token) {
      getMe().then(res => setUser(res.data)).catch(() => {
        localStorage.removeItem('ry_token');
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await apiLogin(email, password);
    const token = res.data.token;
    localStorage.setItem('ry_token', token);
    // Repopulate the user object from GET /api/user/me so that firstLogin and
    // createdAt are present immediately after login. The login response returns
    // a stripped object (id, email, firstName, lastName, onboardingComplete,
    // isAdmin) that omits those fields, which caused BookingModal to treat
    // lockFloorMs as null and show every slot including in-window ones.
    // Fall back to the stripped object only if the follow-up fetch fails.
    let fullUser = res.data.user;
    try {
      const meRes = await getMe();
      fullUser = meRes.data;
    } catch {
      // getMe failed — continue with the stripped login user so login itself
      // never breaks, but firstLogin/createdAt may still be missing.
    }
    setUser(fullUser);
    // Fire-and-forget: capture timezone on login
    const _tz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return null; } })();
    if (_tz) {
      const _base = import.meta.env.VITE_API_URL || 'http://localhost:4000';
      fetch(`${_base}/api/user/timezone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ timezone: _tz }),
      }).catch(() => {});
    }
    return fullUser;
  };

  // Onboarding just completed on the server: say so here too, so the course
  // gate (App.jsx CourseGate) lets them into the dashboard straight away
  // instead of reading the stale "not onboarded" it loaded at sign-in.
  const markOnboarded = () => {
    setUser(u => (u ? { ...u, onboardingComplete: true } : u));
  };

  const logout = () => {
    localStorage.removeItem('ry_token');
    setUser(null);
  };

  return <AuthContext.Provider value={{ user, login, logout, loading, markOnboarded }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
